import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useSetUserPermissions, type UserWithRole } from '@/hooks/useUsers';
import { ALL_PERMISSIONS, PERMISSIONS, type Permission } from '@/lib/permissions';

/**
 * Grant a non-admin user the finance views without making them an admin.
 * One "Finance" switch for the common case, four boxes underneath for the
 * tenant who wants a bookkeeper to see profit but not technician pay.
 */
export function UserPermissionsDialog({ user, open, onOpenChange }: {
  user: UserWithRole | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const save = useSetUserPermissions();
  const [picked, setPicked] = useState<Permission[]>([]);
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (open && user) setPicked(user.permissions);
  }, [open, user]);

  const all = ALL_PERMISSIONS.every((k) => picked.includes(k));
  const toggle = (key: Permission, on: boolean) =>
    setPicked((prev) => (on ? [...new Set([...prev, key])] : prev.filter((k) => k !== key)));

  const submit = () => {
    if (!user) return;
    save.mutate({ userId: user.id, permissions: picked }, { onSuccess: () => onOpenChange(false) });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Permissions for {user?.full_name}</DialogTitle>
          <DialogDescription>
            {isAdmin
              ? 'Administrators see everything. Change their role to limit what they see.'
              : 'What this user can see beyond their own work. Settings stay administrator-only.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <Label htmlFor="perm-all" className="text-sm font-medium">Finance</Label>
              <p className="text-xs text-muted-foreground">Everything below: the finance reports and figures an office manager needs.</p>
            </div>
            <Switch id="perm-all" checked={isAdmin || all} disabled={isAdmin || save.isPending}
              onCheckedChange={(on) => setPicked(on ? [...ALL_PERMISSIONS] : [])} />
          </div>

          <div className="space-y-3 pl-1">
            {PERMISSIONS.map((p) => (
              <div key={p.key} className="flex items-start gap-3">
                <Checkbox id={`perm-${p.key}`} className="mt-0.5" checked={isAdmin || picked.includes(p.key)}
                  disabled={isAdmin || save.isPending} onCheckedChange={(v) => toggle(p.key, v === true)} />
                <div className="space-y-0.5">
                  <Label htmlFor={`perm-${p.key}`} className="text-sm font-medium">{p.label}</Label>
                  <p className="text-xs text-muted-foreground">{p.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={isAdmin || save.isPending}>
            {save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
